import { RouterState } from './../../../router.selectors';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Observable, BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { PageType } from '../../../page-edit-screen/page-type';
import { ContentPageDetailDialogDataViewModel } from '../../../shared/components/content-page-detail-dialog/content-page-detail-dialog-data-view.model';
import { ContentPageDetailDialogModalViewModel } from '../../../shared/components/content-page-detail-dialog/content-page-detail-dialog-modal-view.model';
import { ContentPageDetailDialogComponent } from '../../../shared/components/content-page-detail-dialog/content-page-detail-dialog.component';
import { PageDialogVariant } from '../../../shared/components/page-detail-dialog/page-dialog-variant';
import { createCxbContentPage, createCxbGenrePage, createCxbRedeemPage, loadCxbPageScreen } from '../../page-screen.actions';
import { AppState } from '../../page-screen.reducer';
import { selectSimplePages, selectSimplePagesLoading } from '../../page-screen.selectors';
import { DisplayedType, PageListViewModel, PageScreenViewModel, PageStatus, TabType } from './page-screen.view-model';
import { RedeemPageDetailDialogComponent } from '../../../shared/components/redeem-page-detail-dialog/redeem-page-detail-dialog.component';
import { RedeemPageDetailDialogModalViewModel } from '../../../shared/components/redeem-page-detail-dialog/redeem-page-detail-dialog-modal.view-model';
import { RedeemPageDetailDialogDataViewModel } from '../../../shared/components/redeem-page-detail-dialog/redeem-page-detail-dialog-data.view-model';
import { CONFIG_TOKEN } from 'config-token';
import { Config } from '../../../config';
import { selectRouteParam } from '../../../router.selectors';
import { SimpleGenrePage, SimpleMarketingPage, SimplePage } from '../../../domain/simple-page';

interface Filters {
  contentPageFilter: string;
  redeemPageFilter: string;
  genrePageFilter: string;
  collectionPageFilter: string;
}

@Injectable()
export class PageScreenService implements OnDestroy {
  private readonly destroyed$ = new Subject<void>();

  public readonly _vm$: BehaviorSubject<PageScreenViewModel> = new BehaviorSubject<PageScreenViewModel>({
    subRoute: 'content',
    genrePages: [],
    redeemPages: [],
    marketingPages: [],
    contentPages: [],
    collectionPages: [],
    isLoading: false,
  });
  public readonly vm$: Observable<PageScreenViewModel> = this._vm$.asObservable();

  private readonly filtersChanged$ = new BehaviorSubject<Filters>({
    contentPageFilter: '',
    redeemPageFilter: '',
    genrePageFilter: '',
    collectionPageFilter: '',
  });

  constructor(
    private readonly store: Store<AppState & RouterState>,
    @Inject(CONFIG_TOKEN) private readonly config: Config,
    private readonly dialogService: MatDialog
  ) {
    store.dispatch(loadCxbPageScreen());

    combineLatest([store.select(selectSimplePages), store.select(selectSimplePagesLoading), this.filtersChanged$])
      .pipe(
        map(([pages, isLoading, filters]) => {
          return {
            isLoading,

            contentPages: pages.contentPages
              .map((it) => this.mapDomainPageToListItem(it))
              .filter((it) => this.doesPageMatchFilter(it, filters.contentPageFilter)),

            marketingPages: pages.marketingPages.map((it) => this.mapDomainPageToListItem(it)),

            redeemPages: pages.redeemPages
              .map((it) => this.mapDomainPageToListItem(it))
              .filter((it) => this.doesPageMatchFilter(it, filters.redeemPageFilter)),

            genrePages: pages.genrePages
              .map((it) => this.mapDomainPageToListItem(it))
              .filter((it) => this.doesPageMatchFilter(it, filters.genrePageFilter)),

            subRoute: this._vm$.getValue().subRoute,
          } as PageScreenViewModel;
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe((vm) => this._vm$.next(vm));

    // Route changing is handled separately to avoid remapping all pages
    store.select(selectRouteParam('type')).subscribe((type) => {
      this._vm$.next({ ...this._vm$.getValue(), subRoute: type as TabType });
    });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public filtersChanged(filters: Filters) {
    this.filtersChanged$.next({ ...filters });
  }

  public openCreatePageDialog(pageType: PageType) {
    switch (pageType.domainPageType) {
      case 'Content': {
        this.dialogService
          .open<ContentPageDetailDialogComponent, ContentPageDetailDialogModalViewModel>(ContentPageDetailDialogComponent, {
            data: { variant: PageDialogVariant.NEW, isGenrePage: false },
          })
          .afterClosed()
          .subscribe((value?: ContentPageDetailDialogDataViewModel) => {
            if (value) {
              this.store.dispatch(createCxbContentPage({ data: value }));
            }
          });
        break;
      }
      case 'Redeem': {
        this.dialogService
          .open<RedeemPageDetailDialogComponent, RedeemPageDetailDialogModalViewModel>(RedeemPageDetailDialogComponent, {
            data: { variant: PageDialogVariant.NEW, isGenrePage: false },
          })
          .afterClosed()
          .subscribe((value?: RedeemPageDetailDialogDataViewModel) => {
            if (value) {
              this.store.dispatch(createCxbRedeemPage({ data: value }));
            }
          });
        break;
      }
      case 'Genre': {
        this.dialogService
          .open<ContentPageDetailDialogComponent, ContentPageDetailDialogModalViewModel>(ContentPageDetailDialogComponent, {
            data: { variant: PageDialogVariant.NEW, isGenrePage: true },
          })
          .afterClosed()
          .subscribe((value?: ContentPageDetailDialogDataViewModel) => {
            if (value) {
              this.store.dispatch(createCxbGenrePage({ data: value }));
            }
          });
        break;
      }
    }
  }

  private doesPageMatchFilter(page: PageListViewModel, filter: string): boolean {
    return page.pageName.toLowerCase().includes(filter?.toLowerCase() ?? '');
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private mapDomainPageToListItem(page: SimplePage): PageListViewModel {
    const baseProps: PageListViewModel = {
      displayedType: undefined,
      pageType: page.pageType,
      pageName: page.name,
      status:
        page.draftPageId && page.livePageId ? PageStatus.DRAFT_AND_LIVE : page.livePageId ? PageStatus.LIVE_ONLY : PageStatus.DRAFT_ONLY,
      targetPageId: page.draftPageId ?? page.livePageId,
      lastPublished: page.lastPublishedAt,
      userSegments: [],
      countries: [],
      livePageUrl: '',
      attachedGenre: (page as SimpleGenrePage).genre?.title ?? '',
    };

    switch (page.type) {
      case 'Content':
        return {
          ...baseProps,
          livePageUrl: this.getLivePageUrl(page, `${this.config.redeemPageTargetBaseUrl}/en-ae/${page.slug}`),
        };
      case 'Genre':
        return {
          ...baseProps,
          livePageUrl: page.livePageId ? `${this.config.redeemPageTargetBaseUrl}/en-ae/genre/${page.genre.title}` : '',
        };
      case 'Marketing':
        return {
          ...baseProps,
          displayedType: this.mapMarketingPageToDisplayedType(page),
        };
      case 'Redeem':
        return {
          ...baseProps,
          displayedType: page.isGenericRedeem ? DisplayedType.GenericRedeem : DisplayedType.PartnerRedeem,
          userSegments: page.userSegments.map((it) => it.name),
          countries: page.countries.map((it) => it.name),
          livePageUrl: this.getLivePageUrl(
            page,
            `${this.config.redeemPageTargetBaseUrl}/en-ae/promotion${page.isGenericRedeem ? '' : `/${page.slug}`}`
          ),
        };
    }
  }

  private getLivePageUrl(page: SimplePage, url: string) {
    return page.livePageId ? url : '';
  }

  private mapMarketingPageToDisplayedType(page: SimpleMarketingPage): DisplayedType {
    switch (page.pageType) {
      case PageType.marketingDetails:
        return DisplayedType.DetailedPageWeb;
      case PageType.landing:
        return DisplayedType.LandingPageWeb;
      case PageType.appLandingCotv:
        return DisplayedType.LandingPageCoTv;
      case PageType.appLandingAppleAndroidTv:
        return DisplayedType.LandingPageAppleAndroidTv;
      case PageType.appLandingTablet:
        return DisplayedType.LandingPageTablet;
      case PageType.appLandingMobile:
        return DisplayedType.LandingPageMobile;
    }
  }
}
